---
title: "Wi-Fi QR code: use a guest network, not your main password"
description: "How to create a Wi-Fi QR code, why a guest network is safer, and what to check before printing the code for an office, cafe or apartment."
locale: "en"
date: "2026-07-01"
tags: ["qr", "wifi", "guest-network"]
relatedTools: ["qr-generator"]
translations:
  ru: "qr-kod-dlya-wifi-gostevaya-set"
faq:
  - q: "Can I change the Wi-Fi password inside an already printed QR code?"
    a: "No. Wi-Fi details are stored inside the static QR code. If the password changes, generate and print a new code."
  - q: "Is it safe to print a Wi-Fi QR code with the password?"
    a: "It is safer for a guest network. Anyone who sees the QR code or a photo of it gets the connection details; joining still depends on being within network range."
  - q: "Is the Wi-Fi QR content uploaded to a server?"
    a: "In qrka, QR generation runs locally in the browser through qrcode. The Wi-Fi content does not need a backend upload."
---

A cafe counter has a small sign: `Wi-Fi: CafeGuest`, password in tiny
print, 14 characters long. Every other guest asks again, someone confuses
`O` with `0`, and the staff reads the password out loud. A QR code fixes
that in a minute, as long as it does not contain the main network password.

The [QR code generator](/qr-kod) supports Wi-Fi QR: choose the Wi-Fi type,
enter SSID, security type and password, then download PNG or SVG. The code
is static. A phone camera reads it and offers to join the network.

## What goes into the QR

A Wi-Fi QR stores connection details directly in the code text: network
name, security type and password. Convenient, but not secret. A code on
the wall, a photo in chat or a printout on a table exposes the password to
anyone who can see it.

The sane setup for an office, cafe, studio or apartment with guests is a
separate guest network: isolated from local devices, with a password you
are comfortable showing publicly. Do not put main Wi-Fi credentials into a
QR code when that network can reach the router, NAS, POS, printers or work
infrastructure.

## Before printing

| Check | What to do |
| --- | --- |
| Separate network | Use a guest network, not the main SSID |
| Fresh password | Do not print a password already known by contractors or former staff |
| Enough contrast | Black on white scans more reliably than color experiments |
| Right size | A table sign and a wall poster need different sizes |
| Tested code | Scan it with a phone before printing a batch |

If you rotate the password monthly, rotate the QR monthly too. A static
code cannot be edited after printing. That is the trade-off: no redirect
server and no account, but also no remote update button.

## PNG or SVG

For a small reception sign, a large PNG is fine. For print layout, SVG is
usually better: it scales without blur and survives design tooling more
cleanly. In both cases, keep white space around the matrix. If someone
crops the quiet zone to make the layout tighter, scanning can get worse.

Colored QR codes can work, but Wi-Fi is not the place for drama. People
scan once, often while walking, under glare or from a laptop screen.
Contrast matters more than brand color.

## When not to print it

If the network exposes internal dashboards, file shares, cash registers,
cameras or router admin pages, a QR code on the wall makes the password
public. In that case, configure guest Wi-Fi and client isolation first,
then generate the QR code.

The code itself can be created locally in the browser. Wi-Fi safety,
though, is decided by the network you choose to show everyone.
